(() => {
  const customOrderProductionProxyBase = '/apps/optimum-forms';
  const customOrderDevProxyBase = '/apps/optimum-forms-dev';
  const customOrderProxyBase = () => window.location?.hostname?.endsWith('.shopifypreview.com')
    ? customOrderDevProxyBase
    : customOrderProductionProxyBase;
  function customOrderEndpoint(node, path, datasetKey) {
    const productionEndpoint = `${customOrderProductionProxyBase}${path}`;
    const configured = datasetKey ? String(node?.dataset?.[datasetKey] || '').trim() : '';
    return configured && configured !== productionEndpoint ? configured : `${customOrderProxyBase()}${path}`;
  }
  const defaultEndpoints = {
    'custom-order': '/apps/optimum-forms/custom-orders',
    contact: '/apps/optimum-forms/contact',
    'partner-interest': '/apps/optimum-forms/partner-interest',
  };

  function values(form) {
    const data = {};
    for (const [name, value] of new FormData(form).entries()) {
      if ([...fieldsNamed(form, name)].some((field) => field.type === 'file')) continue;
      if (data[name] === undefined) data[name] = value;
      else data[name] = Array.isArray(data[name]) ? data[name].concat(value) : [data[name], value];
    }
    return data;
  }
  const fieldsNamed = (form, name) => form.querySelectorAll(`[name="${name.replace(/"/g, '\\"')}"]`);
  const checked = (form, name) => form.elements[name]?.checked === true;
  const statusOk = (status) => status === 'received' || status === 'completed' || status === 'partial_failure';
  const contactInquiryTypes = {
    general_inquiry: 'General Inquiry',
    product_support: 'Product Support',
    order_status: 'Order Status',
    new_order: 'New Order Inquiry',
  };
  const excerpt = (text) => text.replace(/<script\b[\s\S]*?<\/script>/gi, ' ').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 160);
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  async function parseResponse(response, endpoint) {
    const text = await response.text();
    const type = response.headers.get('content-type') || '';
    if (type.includes('application/json')) {
      try {
        return text ? JSON.parse(text) : {};
      } catch {
        throw new Error(`Invalid JSON response (HTTP ${response.status}) from ${endpoint}: ${excerpt(text) || response.statusText}`);
      }
    }
    throw new Error(`Unexpected response (HTTP ${response.status}) from ${endpoint}: ${excerpt(text) || response.statusText}`);
  }

  function cookie(name) {
    const match = document.cookie.split('; ').find((item) => item.startsWith(`${name}=`));
    if (!match) return {};
    try {
      return JSON.parse(decodeURIComponent(match.slice(name.length + 1)));
    } catch {
      return {};
    }
  }

  function productPrefill() {
    const params = new URLSearchParams(window.location.search);
    let stored = {};
    try {
      stored = JSON.parse(sessionStorage.getItem('customOrderProduct') || '{}');
    } catch {
      stored = {};
    }
    return {
      handle: params.get('product') || stored.product || '',
      title: params.get('title') || stored.title || '',
      sku: params.get('sku') || stored.sku || '',
    };
  }

  const resumeToken = () => new URLSearchParams(window.location.search).get('resume') || '';

  function showStep(form, step) {
    form.querySelectorAll('[data-step]').forEach((panel) => {
      panel.hidden = panel.dataset.step !== String(step);
    });
    updateHeading(form);
  }

  const cleanPath = (path) => String(path || '').replace(/\/$/, '');
  const pathMatches = (path, target) => path === target || path.endsWith(target);

  function pathStep(form) {
    if (form.dataset.optimumForm !== 'custom-order') return 0;
    const path = cleanPath(window.location.pathname);
    const panel = [...form.querySelectorAll('[data-step-path]')].find((step) => pathMatches(path, cleanPath(step.dataset.stepPath)));
    return Number(panel?.dataset.step || 0);
  }

  function isCompletedPath(form) {
    return form.dataset.optimumForm === 'custom-order'
      && cleanPath(window.location.pathname) === cleanPath(form.dataset.completedPath);
  }

  function pushPageViewed() {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'page_viewed' });
  }

  function pushCorStepCompleted(form, step) {
    if (form.dataset.optimumForm !== 'custom-order' || step < 1 || step > 6) return;
    form._optimumCorCompletedSteps = form._optimumCorCompletedSteps || new Set();
    if (form._optimumCorCompletedSteps.has(step)) return;
    form._optimumCorCompletedSteps.add(step);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'cor_step_completed', cor_step_completed: step });
  }

  function pushCorOrderSubmitted(form) {
    if (form.dataset.optimumForm !== 'custom-order') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'cor_order_submitted' });
  }

  function pushFormSuccess(formName, data) {
    if (formName !== 'contact' && formName !== 'partner-interest') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(formName === 'contact'
      ? { event: 'contact_form_success', contact_inquiry_type: contactInquiryTypes[data.subject] }
      : { event: 'partner_form_success' });
  }

  function pushPath(path, keepSearch = true) {
    if (!path || !window.history?.pushState) return;
    const url = new URL(window.location.href);
    url.pathname = path;
    if (!keepSearch) url.search = '';
    window.history.pushState({ optimumFormPath: path }, '', url);
    pushPageViewed();
  }

  function pushStepPath(form, step) {
    const path = form.querySelector(`[data-step="${step}"]`)?.dataset.stepPath;
    if (path && cleanPath(window.location.pathname) !== cleanPath(path)) pushPath(path);
  }

  function replaceStepPath(form, step) {
    const path = form.querySelector(`[data-step="${step}"]`)?.dataset.stepPath;
    if (!path || cleanPath(window.location.pathname) === cleanPath(path) || !window.history?.replaceState) return;
    const url = new URL(window.location.href);
    url.pathname = path;
    window.history.replaceState({ optimumFormPath: path }, '', url);
  }

  function replaceResumeStepPath(form, step) {
    const path = form.querySelector(`[data-step="${step}"]`)?.dataset.stepPath;
    if (!path || !window.history?.replaceState) return;
    const url = new URL(window.location.href);
    url.pathname = path;
    url.searchParams.delete('resume');
    window.history.replaceState({ optimumFormPath: path }, '', url);
  }

  function pushCompletedPath(form) {
    if (form.dataset.completedPath && cleanPath(window.location.pathname) !== cleanPath(form.dataset.completedPath)) {
      pushPath(form.dataset.completedPath, false);
    }
  }

  function updateHeading(form) {
    const heading = form.closest('.optimum-custom-order-form')?.querySelector('[data-form-heading]');
    const panel = form.querySelector(`[data-step="${form.dataset.currentStep || 1}"]`);
    if (!heading || !panel?.dataset.heading) return;
    const firstName = form.elements.firstName?.value.trim() || 'there';
    heading.replaceChildren(...panel.dataset.heading.split('|').map((line) => {
      const span = document.createElement('span');
      span.textContent = line.replaceAll('{firstName}', firstName);
      return span;
    }));
  }

  function formFieldIsValid(field) {
    const value = String(field.value || '').trim();
    if (field.name === 'email') return emailPattern.test(value);
    if (field.name === 'phone') {
      const digits = value.replace(/\D/g, '');
      return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
    }
    return true;
  }

  function validateFormField(field) {
    if (!field.setCustomValidity || formFieldIsValid(field)) {
      field.setCustomValidity?.('');
      return true;
    }
    field.setCustomValidity(field.name === 'email'
      ? 'Enter a valid email address, including a domain like .com.'
      : 'Enter a valid 10-digit US phone number.');
    return false;
  }

  function fieldCanAdvance(form, field) {
    if (field.disabled || field.type === 'file' || field.type === 'hidden' || !field.required) return true;
    if (form.dataset.optimumForm === 'custom-order') validateFormField(field);
    if (field.type === 'checkbox') return field.checked;
    if (field.type === 'radio') return Array.from(fieldsNamed(form, field.name)).some((item) => item.checked);
    return field.checkValidity ? field.checkValidity() : Boolean(String(field.value || '').trim());
  }

  function stepCanAdvance(form, step) {
    const panel = form.querySelector(`[data-step="${step}"]`);
    if (!panel) return false;
    const fieldsValid = [...panel.querySelectorAll('input, select, textarea')].every((field) => fieldCanAdvance(form, field));
    if (!fieldsValid) return false;
    return [...panel.querySelectorAll('[data-require-one]')].every((group) => Boolean(group.querySelector(`[name="${group.dataset.requireOne}"]:checked`)));
  }

  function allowedStep(form, step) {
    const maxStep = form.querySelectorAll('[data-step]').length || 1;
    const target = Math.min(maxStep, Math.max(1, Number(step) || 1));
    for (let current = 1; current < target; current += 1) {
      if (!stepCanAdvance(form, current)) return current;
    }
    return target;
  }

  function stepIsValid(button) {
    const panel = button.closest('[data-step]');
    if (!panel) return true;
    const isCustomOrder = Boolean(button.closest('[data-optimum-form="custom-order"]'));
    const fieldsValid = [...panel.querySelectorAll('input, select, textarea')].every((field) => {
      if (isCustomOrder) validateFormField(field);
      const valid = field.reportValidity();
      if (valid) field.setCustomValidity?.('');
      return valid;
    });
    if (!fieldsValid) return false;
    return [...panel.querySelectorAll('[data-require-one]')].every((group) => {
      const first = group.querySelector(`[name="${group.dataset.requireOne}"]`);
      if (!first) return true;
      const hasChecked = Boolean(group.querySelector(`[name="${group.dataset.requireOne}"]:checked`));
      first.setCustomValidity(hasChecked ? '' : 'Choose at least one contact preference.');
      const valid = first.reportValidity();
      first.setCustomValidity('');
      return valid;
    });
  }

  function showDurableSuccess(form, status) {
    const success = form.querySelector('[data-form-success]');
    if (success) {
      success.hidden = false;
      success.textContent = status === 'partial_failure'
        ? 'Your request was received. Some follow-up actions may need manual handling.'
        : 'Your request was received.';
    }
    const embeds = form.querySelector('[data-gated-embeds]');
    if (embeds) {
      if (form.dataset.calendlyUrl && !embeds.querySelector('iframe')) {
        const frame = document.createElement('iframe');
        frame.title = 'Schedule consultation';
        frame.loading = 'lazy';
        frame.src = form.dataset.calendlyUrl;
        embeds.append(frame);
      }
      const video = form.querySelector('[data-video-template]');
      if (video && !embeds.querySelector('[data-video-loaded]')) {
        const wrapper = document.createElement('div');
        wrapper.dataset.videoLoaded = 'true';
        wrapper.innerHTML = video.innerHTML;
        embeds.append(wrapper);
      }
      embeds.hidden = false;
    }
    form.reset();
    form.querySelectorAll('[data-step]').forEach((panel) => {
      panel.hidden = true;
    });
    form.querySelector('[data-form-complete]')?.removeAttribute('hidden');
  }

  async function queryAlgolia(form, query) {
    const appId = form.dataset.algoliaAppId;
    const key = form.dataset.algoliaSearchKey;
    const index = form.dataset.algoliaIndex;
    if (!appId || !key || !index) return [];

    const response = await fetch(`https://${appId}-dsn.algolia.net/1/indexes/${encodeURIComponent(index)}/query`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'x-algolia-api-key': key,
        'x-algolia-application-id': appId,
      },
      body: JSON.stringify({ query, hitsPerPage: 8 }),
    });
    if (!response.ok) return [];
    const result = await response.json();
    return result.hits || [];
  }

  function productLabel(data) {
    const title = String(data.productTitle || data.productHandle || '').trim();
    const sku = String(data.productSku || '').trim();
    if (title) return sku ? `${title} (SKU: ${sku})` : title;
    return String(data.productFreeText || '').trim();
  }

  function hitValue(hit, keys) {
    for (const key of keys) {
      const value = key.split('.').reduce((item, part) => item?.[part], hit);
      if (value) return value;
    }
    return '';
  }

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[char]);
  }

  function highlight(value, query) {
    const escaped = escapeHtml(value);
    const term = query.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return term ? escaped.replace(new RegExp(`(${term})`, 'ig'), '<mark>$1</mark>') : escaped;
  }

  function productHit(hit) {
    return {
      handle: hitValue(hit, ['handle']),
      title: hitValue(hit, ['title', 'name', 'product_title', 'named_tags.title']),
      sku: hitValue(hit, ['meta.details.sku']),
      image: hitValue(hit, ['image', 'product_image', 'featured_image', 'featuredImage.url', 'images.0']),
    };
  }

  function productResultNode(product, query, tagName = 'button') {
    const node = document.createElement(tagName);
    node.className = `optimum-custom-order-form__result${product.image ? '' : ' optimum-custom-order-form__result--no-image'}`;
    node.dataset.handle = product.handle || '';
    node.dataset.title = product.title || '';
    node.dataset.sku = product.sku || '';
    node.dataset.image = product.image || '';
    if (tagName === 'button') {
      node.type = 'button';
      node.dataset.algoliaProduct = 'true';
    }
    if (product.image) {
      const image = document.createElement('img');
      image.src = product.image;
      image.alt = '';
      image.loading = 'lazy';
      node.append(image);
    }
    const content = document.createElement('span');
    content.innerHTML = `<strong>${highlight(product.title, query)}</strong>${product.sku ? `<span>SKU: ${escapeHtml(product.sku)}</span>` : ''}`;
    node.append(content);
    return node;
  }

  function renderProductResults(form, hits, query) {
    const list = form.querySelector('[data-product-results]');
    if (!list) return;
    list.replaceChildren(...hits.map((hit) => productResultNode(productHit(hit), query)));
    list.hidden = !hits.length;
  }

  function renderSelectedProduct(form, product) {
    const selected = form.querySelector('[data-selected-product]');
    if (!selected) return;
    if (!product?.title) {
      selected.replaceChildren();
      selected.setAttribute('hidden', '');
      return;
    }
    selected.replaceChildren(productResultNode(product, '', 'div'));
    const clear = document.createElement('button');
    clear.type = 'button';
    clear.className = 'optimum-custom-order-form__clear-product';
    clear.dataset.clearProduct = 'true';
    clear.textContent = 'Clear';
    selected.children[0]?.append(clear);
    selected.removeAttribute('hidden');
  }

  function selectCatalogProduct(form, product) {
    const title = product.title || product.handle || '';
    form.elements.productHandle.value = product.handle || '';
    form.elements.productTitle.value = title;
    form.elements.productSku.value = product.sku || '';
    form.elements.productFreeText.value = '';
    const search = form.querySelector('[data-product-search]');
    if (search) search.value = title;
    renderSelectedProduct(form, { ...product, title });
  }

  function useManualProductText(form, value) {
    form.elements.productHandle.value = '';
    form.elements.productTitle.value = '';
    form.elements.productSku.value = '';
    form.elements.productFreeText.value = value;
    renderSelectedProduct(form, null);
  }

  function syncProductSearchState(form) {
    const search = form.querySelector('[data-product-search]');
    if (!search) return;
    const title = String(form.elements.productTitle?.value || form.elements.productHandle?.value || '').trim();
    if (title) {
      form.elements.productFreeText.value = '';
      search.value = title;
      renderSelectedProduct(form, {
        handle: form.elements.productHandle?.value || '',
        title,
        sku: form.elements.productSku?.value || '',
        image: '',
      });
    } else {
      search.value = form.elements.productFreeText?.value || '';
      renderSelectedProduct(form, null);
    }
  }

  async function hydrateSelectedProductFromAlgolia(form, store) {
    const handle = String(form.elements.productHandle?.value || '').trim();
    const title = String(form.elements.productTitle?.value || '').trim();
    const savedSku = String(form.elements.productSku?.value || '').trim();
    if (!handle && !title) return;

    const fallbackTitle = title || handle;
    const stillSelected = () => String(form.elements.productHandle?.value || '').trim() === handle
      && String(form.elements.productTitle?.value || '').trim() === title;
    form.elements.productFreeText.value = '';
    const search = form.querySelector('[data-product-search]');
    if (search) search.value = fallbackTitle;
    renderSelectedProduct(form, { handle, title: fallbackTitle, sku: savedSku, image: '' });

    try {
      const products = (await queryAlgolia(form, title || handle)).map(productHit);
      if (!stillSelected()) return;
      const titleLower = title.toLowerCase();
      const match = (handle && products.find((product) => product.handle === handle))
        || (titleLower && products.find((product) => product.title.toLowerCase() === titleLower));
      if (!match) return;

      const nextTitle = title || match.title || handle;
      if (!title && match.title) form.elements.productTitle.value = match.title;
      if (match.sku) form.elements.productSku.value = match.sku;
      renderSelectedProduct(form, {
        handle,
        title: nextTitle,
        sku: match.sku || savedSku,
        image: match.image || '',
      });
      saveFormState(form, store);
    } catch {
      if (!stillSelected()) return;
      renderSelectedProduct(form, { handle, title: fallbackTitle, sku: savedSku, image: '' });
    }
  }

  function clearCatalogProduct(form, store) {
    form.elements.productHandle.value = '';
    form.elements.productTitle.value = '';
    form.elements.productSku.value = '';
    form.elements.productFreeText.value = '';
    const search = form.querySelector('[data-product-search]');
    if (search) {
      search.value = '';
      search.focus?.();
    }
    renderSelectedProduct(form, null);
    const list = form.querySelector('[data-product-results]');
    if (list) {
      list.replaceChildren();
      list.hidden = true;
    }
    saveFormState(form, store);
  }

  function customOrderPayload(form, data, fileKeys) {
    const product = productLabel(data);
    const checkedSizes = Array.from(form.querySelectorAll('[name="productSizes"]:checked')).map((input) => input.value);
    const productSizes = checkedSizes.length ? checkedSizes.join(', ') : data.productSizes;
    const contactPreferences = Array.from(form.querySelectorAll('[name="contactPreferences"]:checked')).map((input) => input.value);
    if (!product) throw new Error('Choose a product or enter product details.');
    if (!productSizes) throw new Error('Enter product sizes.');
    if (!contactPreferences.length) throw new Error('Choose at least one contact preference.');

    return {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      contactPreferences,
      lineOfWork: data.lineOfWork,
      lineOfWorkOther: data.lineOfWork === 'other' ? data.lineOfWorkOther || undefined : undefined,
      product,
      productSizes,
      additionalInfo: data.additionalInfo || undefined,
      zipCode: data.zipCode,
      projectStartDate: data.projectStartDate,
      fileKeys: fileKeys.length ? fileKeys : undefined,
      marketingSmsConsent: checked(form, 'marketingSmsConsent'),
      nonMarketingSmsConsent: checked(form, 'nonMarketingSmsConsent'),
    };
  }

  function compactPayload(payload) {
    return Object.fromEntries(Object.entries(payload).filter(([, value]) => {
      if (value == null) return false;
      if (typeof value === 'string') return value.trim() !== '';
      if (Array.isArray(value)) return value.length > 0;
      return true;
    }));
  }

  function customOrderProgressPayload(form) {
    const data = values(form);
    const checkedSizes = Array.from(form.querySelectorAll('[name="productSizes"]:checked')).map((input) => input.value);
    return compactPayload({
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      contactPreferences: Array.from(form.querySelectorAll('[name="contactPreferences"]:checked')).map((input) => input.value),
      marketingSmsConsent: checked(form, 'marketingSmsConsent'),
      nonMarketingSmsConsent: checked(form, 'nonMarketingSmsConsent'),
      lineOfWork: data.lineOfWork,
      lineOfWorkOther: data.lineOfWork === 'other' ? data.lineOfWorkOther : undefined,
      productHandle: data.productHandle,
      productTitle: data.productTitle,
      productSku: data.productSku,
      productFreeText: data.productFreeText,
      productSizes: checkedSizes.length ? checkedSizes : data.productSizes,
      additionalInfo: data.additionalInfo,
      zipCode: data.zipCode,
      projectStartDate: data.projectStartDate,
    });
  }

  async function checkpointCustomOrderProgress(form, store, completedStep) {
    if (form.dataset.optimumForm !== 'custom-order') return;
    const endpoint = customOrderEndpoint(form, '/custom-orders/progress', 'progressEndpoint');
    const turnstileToken = await ensureTurnstileToken(form);
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        submissionId: store.id,
        turnstileToken,
        completedStep,
        attribution: cookie('opti_params'),
        payload: customOrderProgressPayload(form),
      }),
    });
    const result = await parseResponse(response, endpoint);
    if (!response.ok || !result.ok) throw new Error(result.message || 'Progress checkpoint failed');
    resetTurnstile(form);
    ensureTurnstileToken(form).catch(() => undefined);
    return result;
  }

  async function loadCustomOrderResume(form, store) {
    const token = resumeToken();
    if (form.dataset.optimumForm !== 'custom-order' || !token) return null;
    try {
      const endpoint = `${customOrderEndpoint(form, '/custom-orders/resume', 'resumeEndpoint')}?token=${encodeURIComponent(token)}`;
      const response = await fetch(endpoint);
      const result = await parseResponse(response, endpoint);
      if (!response.ok || !result.ok || result.status !== 'active' || !result.submissionId) return null;
      const payload = result.payload || {};
      const restoredAttachments = Array.isArray(result.attachments) ? result.attachments : [];
      restore(form, payload);
      syncLineOfWorkOther(form);
      form._optimumAttachments = restoredAttachments;
      store.replace({ id: result.submissionId, data: { step: String(result.nextStep), payload: values(form), attachments: restoredAttachments } });
      return result;
    } catch {
      return null;
    }
  }

  function queueProgressCheckpoint(form, task) {
    form._optimumProgressQueue = (form._optimumProgressQueue || Promise.resolve())
      .catch(() => undefined)
      .then(task);
    return form._optimumProgressQueue;
  }

  function contactPayload(form, data) {
    return {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      subject: data.subject,
      message: data.message || undefined,
      marketingSmsConsent: checked(form, 'marketingSmsConsent'),
      nonMarketingSmsConsent: checked(form, 'nonMarketingSmsConsent'),
    };
  }

  function partnerPayload(form, data) {
    return {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      lineOfWork: data.lineOfWork,
      lineOfWorkOther: data.lineOfWorkOther || undefined,
      contractorsLicense: data.contractorsLicense || undefined,
      website: data.website || undefined,
      message: data.message || undefined,
      marketingSmsConsent: checked(form, 'marketingSmsConsent'),
      nonMarketingSmsConsent: checked(form, 'nonMarketingSmsConsent'),
    };
  }

  function restore(form, payload = {}) {
    Object.entries(payload).forEach(([name, value]) => {
      const fields = fieldsNamed(form, name);
      fields.forEach((field) => {
        if (field.type === 'file') return;
        if (field.type === 'checkbox' || field.type === 'radio') field.checked = Array.isArray(value) ? value.includes(field.value) : value === field.value || value === true;
        else field.value = value;
      });
    });
  }

  function syncLineOfWorkOther(form) {
    if (form.dataset.optimumForm !== 'custom-order') return;
    const field = form.elements.lineOfWorkOther;
    if (!field) return;
    const isOther = Array.from(fieldsNamed(form, 'lineOfWork')).some((input) => input.checked && input.value === 'other');
    field.disabled = !isOther;
    field.required = isOther;
    if (!isOther) field.value = '';
  }

  const attachments = (form) => {
    form._optimumAttachments = form._optimumAttachments || [];
    return form._optimumAttachments;
  };

  function saveFormState(form, store) {
    return store.save({
      step: form.dataset.currentStep || '1',
      payload: values(form),
      attachments: attachments(form),
    });
  }

  function syncFileKeys(form) {
    const fileInput = form.querySelector('[data-file-input]');
    if (fileInput) fileInput.dataset.fileKeys = JSON.stringify(attachments(form).map(({key}) => key));
  }

  const formatBytes = (size) => `${Math.max(1, Math.round(size / 1024))} KB`;

  function renderAttachments(form) {
    const list = form.querySelector('[data-attachment-list]');
    if (!list) return;
    list.replaceChildren(...attachments(form).map((file) => {
      const item = document.createElement('div');
      item.className = 'optimum-custom-order-form__attachment';
      if (file.previewUrl && String(file.type || '').startsWith('image/')) {
        const image = document.createElement('img');
        image.src = file.previewUrl;
        image.alt = '';
        item.append(image);
      } else {
        const icon = document.createElement('span');
        icon.className = 'optimum-custom-order-form__file-icon';
        icon.textContent = 'FILE';
        item.append(icon);
      }
      const label = document.createElement('span');
      label.textContent = file.name;
      const meta = document.createElement('small');
      meta.textContent = formatBytes(file.size);
      label.append(meta);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.dataset.removeAttachment = file.key;
      remove.textContent = 'Remove';
      item.append(label, remove);
      return item;
    }));
    syncFileKeys(form);
  }

  function refreshSubmit(form) {
    const button = form.querySelector('[type="submit"]');
    if (!button) return;
    const uploadsPending = form.dataset.uploadsPending === 'true';
    const submitting = form.dataset.submitting === 'true';
    button.disabled = uploadsPending || submitting;
  }

  function clearTurnstile(form, message) {
    delete form.dataset.turnstileToken;
    const status = form.querySelector('[data-upload-status]');
    if (message && status) status.textContent = message;
    refreshSubmit(form);
  }

  function resetTurnstile(form, message = '') {
    clearTurnstile(form, message);
    if (window.turnstile?.reset && form.dataset.turnstileWidgetId) window.turnstile.reset(form.dataset.turnstileWidgetId);
  }

  function receiveTurnstileToken(form, token) {
    form.dataset.turnstileToken = token;
    delete form._optimumTurnstileTokenPromise;
    (form._optimumTurnstileWaiters || []).splice(0).forEach(({resolve}) => resolve(token));
    refreshSubmit(form);
  }

  function rejectTurnstileToken(form) {
    clearTurnstile(form, '');
    delete form._optimumTurnstileTokenPromise;
    (form._optimumTurnstileWaiters || []).splice(0).forEach(({reject}) => reject(new Error('Verification failed. Please try again.')));
  }

  function ensureTurnstileToken(form) {
    if (!form.dataset.turnstileSiteKey || form.dataset.turnstileToken) return Promise.resolve(form.dataset.turnstileToken || '');
    if (form._optimumTurnstileTokenPromise) return form._optimumTurnstileTokenPromise;
    form._optimumTurnstileTokenPromise = new Promise((resolve, reject) => {
      form._optimumTurnstileWaiters = form._optimumTurnstileWaiters || [];
      form._optimumTurnstileWaiters.push({resolve, reject});
      form._optimumTurnstileExecutionPending = true;
      setupTurnstile(form);
      if (window.turnstile?.execute && form.dataset.turnstileWidgetId) {
        form._optimumTurnstileExecutionPending = false;
        window.turnstile.execute(form.dataset.turnstileWidgetId);
      }
    });
    return form._optimumTurnstileTokenPromise;
  }

  function setupTurnstile(form) {
    const widget = form.querySelector('[data-turnstile-widget]');
    if (!widget || !form.dataset.turnstileSiteKey) return;
    let attempts = 0;
    const render = () => {
      if (form.dataset.turnstileWidgetId) return true;
      if (!window.turnstile?.render) return false;
      form.dataset.turnstileWidgetId = String(window.turnstile.render(widget, {
        sitekey: form.dataset.turnstileSiteKey,
        action: 'turnstile-spin-v2',
        execution: 'execute',
        appearance: 'interaction-only',
        theme: 'auto',
        size: 'flexible',
        callback: (token) => receiveTurnstileToken(form, token),
        'expired-callback': () => rejectTurnstileToken(form),
        'error-callback': () => rejectTurnstileToken(form),
        'timeout-callback': () => rejectTurnstileToken(form),
      }));
      if (form._optimumTurnstileExecutionPending && window.turnstile.execute) {
        form._optimumTurnstileExecutionPending = false;
        window.turnstile.execute(form.dataset.turnstileWidgetId);
      }
      refreshSubmit(form);
      return true;
    };
    if (render()) return;
    const timer = setInterval(() => {
      attempts += 1;
      if (render()) clearInterval(timer);
      else if (attempts > 50) {
        clearInterval(timer);
        rejectTurnstileToken(form);
      }
    }, 100);
  }

  async function uploadSelectedFiles(form, store, fileInput) {
    if (!fileInput || !window.OptimumFileUploader) return;
    fileInput.dataset.uploadEndpoint = customOrderEndpoint(fileInput, '/uploads/authorize', 'uploadEndpoint');
    const files = Array.from(fileInput.files || []);
    if (!files.length) return;
    const status = form.querySelector('[data-upload-status]');
    const maxFiles = Number(fileInput.dataset.maxFiles || 5);
    if (attachments(form).length + files.length > maxFiles) {
      if (status) status.textContent = `Upload up to ${maxFiles} files.`;
      fileInput.value = '';
      return;
    }
    form.dataset.uploadsPending = 'true';
    refreshSubmit(form);
    if (!fileInput.files?.length) {
      if (status) status.textContent = '';
      return;
    }
    try {
      if (status) status.textContent = 'Uploading files...';
      const token = await ensureTurnstileToken(form);
      const keys = await window.OptimumFileUploader.upload(fileInput, store.id, token);
      keys.forEach((key, index) => attachments(form).push({
        key,
        name: files[index].name,
        size: files[index].size,
        type: files[index].type,
        previewUrl: files[index].type.startsWith('image/') ? URL.createObjectURL(files[index]) : '',
      }));
      renderAttachments(form);
      saveFormState(form, store);
      resetTurnstile(form);
    } catch (error) {
      if (status) status.textContent = error.message || 'Upload failed';
      throw error;
    } finally {
      delete form.dataset.uploadsPending;
      fileInput.value = '';
      refreshSubmit(form);
    }
  }

  async function deleteAttachment(form, store, attachment) {
    const status = form.querySelector('[data-upload-status]');
    form.dataset.uploadsPending = 'true';
    refreshSubmit(form);
    try {
      const token = await ensureTurnstileToken(form);
      const endpoint = customOrderEndpoint(form, '/uploads/delete', 'uploadDeleteEndpoint');
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          submissionId: store.id,
          fileKey: attachment.key,
          turnstileToken: token,
        }),
      });
      const result = await parseResponse(response, endpoint);
      if (!response.ok || !result.ok) throw new Error(result.message || 'Upload deletion failed');
      form._optimumAttachments = attachments(form).filter(({key}) => key !== attachment.key);
      if (String(attachment.previewUrl || '').startsWith('blob:')) URL.revokeObjectURL(attachment.previewUrl);
      if (status) status.textContent = attachments(form).length ? 'Files attached.' : '';
      renderAttachments(form);
      saveFormState(form, store);
      resetTurnstile(form);
    } catch (error) {
      if (status) status.textContent = error.message || 'Upload deletion failed';
      throw error;
    } finally {
      delete form.dataset.uploadsPending;
      refreshSubmit(form);
    }
  }

  function submissionErrorMessage(result) {
    const fieldErrors = Object.entries(result?.fieldErrors || {})
      .flatMap(([field, errors]) => (errors || []).filter(Boolean).map((error) => `${field}: ${error}`));
    if (!fieldErrors.length) return result?.message || 'Submission failed';
    return `${result?.message || 'Submission failed'}: ${fieldErrors.join('; ')}`;
  }

  function validateForm(form) {
    return [...form.querySelectorAll('input, select, textarea')].every((field) => {
      validateFormField(field);
      const valid = field.reportValidity();
      if (valid) field.setCustomValidity?.('');
      return valid;
    });
  }

  function finishSuccessfulSubmission(form, store, status) {
    store.clear();
    form._optimumAttachments = [];
    syncFileKeys(form);
    resetTurnstile(form, '');
    pushCorStepCompleted(form, 6);
    showDurableSuccess(form, status);
    pushCorOrderSubmitted(form);
    pushCompletedPath(form);
  }

  async function submit(form, store) {
    const formName = form.dataset.optimumForm;
    await Promise.resolve(form._optimumProgressQueue).catch(() => undefined);
    const data = values(form);
    data.turnstileToken = form.dataset.turnstileToken || data['cf-turnstile-response'] || '';
    if (Boolean(form.dataset.turnstileSiteKey) && !data.turnstileToken) {
      data.turnstileToken = await ensureTurnstileToken(form);
    }
    const fileKeys = formName === 'custom-order' ? attachments(form).map(({key}) => key) : [];
    const payload = formName === 'custom-order'
      ? customOrderPayload(form, data, fileKeys)
      : formName === 'contact'
        ? contactPayload(form, data)
        : partnerPayload(form, data);
    const endpoint = formName === 'custom-order'
      ? customOrderEndpoint(form, '/custom-orders', 'endpoint')
      : form.dataset.endpoint || defaultEndpoints[formName];
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        submissionId: store.id,
        turnstileToken: data.turnstileToken,
        attribution: cookie('opti_params'),
        payload,
      }),
    });
    const result = await parseResponse(response, endpoint);
    if (!response.ok || !statusOk(result.status)) throw new Error(submissionErrorMessage(result));
    pushFormSuccess(formName, data);
    finishSuccessfulSubmission(form, store, result.status);
  }

  async function bind(form) {
    const store = window.OptimumFormState.create(form.dataset.optimumForm);
    const saved = store.load();
    const resumed = form.dataset.optimumForm === 'custom-order' && resumeToken()
      ? await loadCustomOrderResume(form, store)
      : null;
    if (!resumed) {
      restore(form, saved.payload);
      form._optimumAttachments = Array.isArray(saved.attachments) ? saved.attachments : [];
      syncLineOfWorkOther(form);
    }
    if (form.dataset.optimumForm === 'custom-order') {
      const product = productPrefill();
      const hasProduct = ['productHandle', 'productTitle', 'productSku', 'productFreeText']
        .some((name) => String(form.elements[name]?.value || '').trim());
      if (!hasProduct) {
        form.elements.productHandle.value = product.handle;
        form.elements.productTitle.value = product.title;
        form.elements.productSku.value = product.sku;
      }
      syncProductSearchState(form);
      form._optimumProductHydration = hydrateSelectedProductFromAlgolia(form, store);
    }
    const routedStep = pathStep(form);
    const completedPath = isCompletedPath(form);
    if (completedPath && !resumed) showDurableSuccess(form, 'received');
    if (!completedPath || resumed) {
      const requestedStep = Number(resumed?.nextStep || routedStep || saved.step || form.dataset.currentStep || 1);
      const step = allowedStep(form, requestedStep);
      form.dataset.currentStep = String(step);
      showStep(form, step);
      if (resumed) replaceResumeStepPath(form, step);
      else if (routedStep && step !== requestedStep) replaceStepPath(form, step);
    }
    saveFormState(form, store);
    updateHeading(form);
    renderAttachments(form);
    setupTurnstile(form);
    if (form.dataset.optimumForm === 'custom-order') ensureTurnstileToken(form).catch(() => undefined);
    refreshSubmit(form);

    form.addEventListener('input', (event) => {
      validateFormField(event.target);
      saveFormState(form, store);
      updateHeading(form);
    });
    form.addEventListener('change', (event) => {
      if (event.target.name !== 'lineOfWork') return;
      syncLineOfWorkOther(form);
      saveFormState(form, store);
    });
    form.querySelector('[data-file-input]')?.addEventListener('change', async (event) => {
      const status = form.querySelector('[data-upload-status]');
      if (status) status.textContent = event.target.files?.length ? 'Uploading files...' : '';
      uploadSelectedFiles(form, store, event.target).catch((error) => {
        if (status) status.textContent = error.message || 'Upload failed';
      });
    });
    form.addEventListener('click', async (event) => {
      const remove = event.target.closest('[data-remove-attachment]');
      if (remove) {
        const attachment = attachments(form).find(({key}) => key === remove.dataset.removeAttachment);
        if (attachment) await deleteAttachment(form, store, attachment).catch(() => undefined);
        return;
      }

      if (event.target.closest('[data-clear-product]')) {
        clearCatalogProduct(form, store);
        return;
      }

      const next = event.target.closest('[data-next-step]');
      const previous = event.target.closest('[data-previous-step]');
      if (next || previous) {
        if (next && !stepIsValid(next)) return;
        const completedStep = Number(next?.closest('[data-step]')?.dataset.step || form.dataset.currentStep || 1);
        if (next && completedStep <= 5) {
          if (form.dataset.checkpointing === 'true') return;
          form.dataset.checkpointing = 'true';
          next.disabled = true;
          try {
            await queueProgressCheckpoint(form, () => checkpointCustomOrderProgress(form, store, completedStep));
          } catch {
            const failure = form.querySelector('[data-form-error]');
            if (failure) {
              failure.hidden = false;
              failure.textContent = 'Verification failed. Please try again.';
            }
            resetTurnstile(form);
            return;
          } finally {
            delete form.dataset.checkpointing;
            next.disabled = false;
          }
        }
        if (next) pushCorStepCompleted(form, completedStep);
        const step = Number(form.dataset.currentStep || 1) + (next ? 1 : -1);
        const maxStep = form.querySelectorAll('[data-step]').length;
        form.dataset.currentStep = String(Math.min(maxStep, Math.max(1, step)));
        saveFormState(form, store);
        showStep(form, form.dataset.currentStep);
        pushStepPath(form, form.dataset.currentStep);
      }

      const option = event.target.closest('[data-algolia-product]');
      if (option) {
        selectCatalogProduct(form, {
          handle: option.dataset.handle || '',
          title: option.dataset.title || option.textContent.trim(),
          sku: option.dataset.sku || '',
          image: option.dataset.image || '',
        });
        const list = form.querySelector('[data-product-results]');
        if (list) list.hidden = true;
        saveFormState(form, store);
      }
    });
    const productSearch = form.querySelector('[data-product-search]');
    const searchProducts = async (event) => {
      const value = event.target.value;
      if (value !== String(form.elements.productTitle?.value || '').trim()) useManualProductText(form, value);
      const results = await queryAlgolia(form, event.target.value);
      renderProductResults(form, results, event.target.value);
    };
    productSearch?.addEventListener('input', searchProducts);
    productSearch?.addEventListener('focus', searchProducts);
    window.addEventListener('popstate', () => {
      if (isCompletedPath(form)) {
        showDurableSuccess(form, 'received');
        pushPageViewed();
        return;
      }
      const step = pathStep(form);
      if (!step) return;
      const allowed = allowedStep(form, step);
      form.dataset.currentStep = String(allowed);
      saveFormState(form, store);
      showStep(form, allowed);
      if (allowed !== step) replaceStepPath(form, allowed);
      pushPageViewed();
    });
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (form.dataset.submitting === 'true') return;
      if (!validateForm(form)) return;
      const button = form.querySelector('[type="submit"]');
      const failure = form.querySelector('[data-form-error]');
      form.dataset.submitting = 'true';
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      }
      if (failure) failure.hidden = true;
      submit(form, store).catch((error) => {
        if (failure) {
          failure.hidden = false;
          failure.textContent = error.message;
        }
      }).finally(() => {
        delete form.dataset.submitting;
        if (button) {
          button.removeAttribute('aria-busy');
        }
        refreshSubmit(form);
      });
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-optimum-form]').forEach((form) => {
      Promise.resolve(bind(form)).catch((error) => console.warn('Optimum form initialization failed', error));
    });
  });

  window.OptimumFormClient = { bind, checkpointCustomOrderProgress, contactPayload, customOrderEndpoint, customOrderFieldIsValid: formFieldIsValid, customOrderProgressPayload, customOrderProxyBase, formFieldIsValid, customOrderPayload, ensureTurnstileToken, finishSuccessfulSubmission, loadCustomOrderResume, parseResponse, productHit, productLabel, productResultNode, queryAlgolia, queueProgressCheckpoint, renderProductResults, resumeToken, setupTurnstile, showDurableSuccess, statusOk, submissionErrorMessage, validateForm, validateFormField };
})();
